// Top-level React error boundary (#180). This replaces the old Sentry.ErrorBoundary that wrapped
// the router: it's a real React error boundary (componentDidCatch), so it catches render/lifecycle
// throws anywhere in the component tree — the same catch surface Sentry's boundary had, NOT the
// narrower loader/route-error surface of React Router's own ErrorBoundary export. On a caught error
// it reports to Better Stack via reportError and renders the same fallback copy the app showed
// before.
import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportError } from "./errorReporter";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, {
      feature: "react-render",
      extra: { componentStack: info.componentStack },
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="p-8 text-center">
          An error has occurred. We've been notified and will fix it soon. Please
          refresh the page.
        </div>
      );
    }
    return this.props.children;
  }
}
